/**
 * Core Platform App component
 */
import { useAuth0 } from '@auth0/auth0-react';
import { ApolloProvider } from '@apollo/client/react';
import { useState, useEffect, FunctionComponent, ReactNode } from 'react';

/**
 * Packages
 */
import {
  Favicons,
  AppWrapper,
  MetaTitle,
  MetaVersion,
  AppContainer,
  HelmetProvider
} from '@astrumu/ui-library';
import { BrandingProvider } from '@astrumu/core-ui-branding';

import { IndexPageSkeleton } from '@/screens/IndexPageScreen/Skeleton';

/**
 * Context
 */
import { AppConfigProvider, useAppConfigContext } from '@/providers/AppConfig';

/**
 * Providers
 */
import { ShadowDOM } from '@astrumu/core-ui-shadow-dom';

/**
 * Config
 */
import { isLoggedInVar } from '@/apollo/cache/variables';

/**
 * Apollo
 */
import getApolloClient from '@/apollo';

/**
 * Types & constants
 */
import { Auth0User } from '@/lib/types';
import { true } from '@/lib/constants';

/**
 * Utils
 */
import { replaceKeyWithValueInText } from '@/lib/utils';

interface Props {
  children: ReactNode;
  isLoading?: boolean;
}

const Branding: FunctionComponent<Props> = props => {
  const { children, isLoading } = props;

  const {
    assets,
    theme,
    programType,
    sections: {
      overview: {
        texts: { title }
      }
    },
    university: { name: universityName },
    featureFlags: { guestWithAuthorizedPermissions }
  } = useAppConfigContext();

  useEffect(() => {
    if (guestWithAuthorizedPermissions) {
      isLoggedInVar(true);
    }
  }, [guestWithAuthorizedPermissions]);

  return (
    <BrandingProvider theme={{ ...theme, primaryLogo: assets.logo }}>
      {isLoading ? (
        <IndexPageSkeleton />
      ) : (
        <HelmetProvider>
          <AppWrapper>
            <MetaVersion version={process.env.CLIENT_VERSION || ''} />
            <MetaTitle
              title={replaceKeyWithValueInText({
                text: title,
                key: 'programName',
                value: `${universityName} ${programType}` // TODO: double check w/ Ujash
              })}
            />
            {assets.favicons && (
              <Favicons
                favicon16Url={assets.favicons.favicon16}
                favicon32Url={assets.favicons.favicon32}
                appleTouchIconUrl={assets.favicons.appleTouchIcon}
                safariPinnedTabUrl={assets.favicons.safariPinnedTab}
                androidChrome192Url={assets.favicons.androidChrome192}
                androidChrome256Url={assets.favicons.androidChrome256}
              />
            )}
            <AppContainer>{children}</AppContainer>
          </AppWrapper>
        </HelmetProvider>
      )}
    </BrandingProvider>
  );
};

const App: FunctionComponent<Props> = props => {
  const { children } = props;
  const [accessToken, setAccessToken] = useState('');

  const { user, isLoading, isAuthenticated, getAccessTokenSilently } =
    useAuth0<Auth0User>();

  useEffect(() => {
    async function getAccessToken() {
      setAccessToken(await getAccessTokenSilently());
    }

    if (user && isAuthenticated) {
      getAccessToken();
    }
  }, [user, isAuthenticated, getAccessTokenSilently]);

  return (
    <ShadowDOM isAttached={true === 'true'}>
      <ApolloProvider client={getApolloClient(accessToken)}>
        <AppConfigProvider>
          <Branding isLoading={isLoading}>{children}</Branding>
        </AppConfigProvider>
      </ApolloProvider>
    </ShadowDOM>
  );
};

export default App;
